import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Send, 
  Paperclip, 
  Mic, 
  MicOff, 
  Bot, 
  User, 
  Clock, 
  CheckCircle,
  Zap,
  MessageSquare,
  FileText
} from 'lucide-react';
import { Button } from '../ui/button.js';

interface Message {
  id: string;
  type: 'user' | 'ai' | 'system';
  content: string;
  timestamp: Date;
  metadata?: {
    ticketId?: string;
    priority?: 'low' | 'medium' | 'high' | 'critical';
    category?: string;
    suggestedActions?: string[];
    attachments?: string[];
  };
}

interface TicketSuggestion {
  title: string;
  description: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  category: string;
  confidence: number;
  suggestedAssignee?: string;
}

interface ConversationalTicketingProps {
  onTicketCreated?: (ticket: any) => void;
  onMessageSent?: (message: Message) => void;
  className?: string;
}

const ConversationalTicketing: React.FC<ConversationalTicketingProps> = ({
  onTicketCreated,
  onMessageSent,
  className = ''
}) => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      type: 'ai',
      content: "Hi! I'm your AI support assistant. Describe your issue and I'll help you create a ticket or find a solution. You can also attach files, screenshots, or provide any relevant details.",
      timestamp: new Date(),
      metadata: {
        suggestedActions: ['Describe your issue', 'Upload screenshot', 'Check knowledge base']
      }
    }
  ]);
  
  const [inputValue, setInputValue] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [ticketSuggestion, setTicketSuggestion] = useState<TicketSuggestion | null>(null);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [isOnline] = useState(true);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() && attachments.length === 0) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: inputValue,
      timestamp: new Date(),
      metadata: {
        attachments: attachments.map(f => f.name)
      }
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setAttachments([]);
    setIsTyping(true);

    if (onMessageSent) {
      onMessageSent(userMessage);
    }

    setTimeout(() => {
      const aiResponse = generateAIResponse(userMessage);
      setMessages(prev => [...prev, aiResponse]);
      setIsTyping(false);
      
      if (shouldSuggestTicket(userMessage.content)) {
        const suggestion = generateTicketSuggestion(userMessage.content);
        setTicketSuggestion(suggestion);
      }
    }, 1500);
  };

  const generateAIResponse = (_userMessage: Message): Message => {
    const responses = [
      "I understand your issue. Let me analyze this and suggest the best course of action.",
      "Based on your description, I can help you create a ticket or find relevant solutions.",
      "I've identified some potential solutions. Would you like me to create a ticket or search our knowledge base?",
      "This seems like a common issue. I can either create a ticket or show you some quick fixes."
    ];

    return {
      id: (Date.now() + 1).toString(),
      type: 'ai',
      content: responses[Math.floor(Math.random() * responses.length)],
      timestamp: new Date(),
      metadata: {
        suggestedActions: ['Create ticket', 'Search knowledge base', 'Get quick fix', 'Escalate to human']
      }
    };
  };

  const shouldSuggestTicket = (content: string): boolean => {
    const ticketKeywords = ['error', 'bug', 'issue', 'problem', 'broken', 'not working', 'help'];
    return ticketKeywords.some(keyword => content.toLowerCase().includes(keyword));
  };

  const generateTicketSuggestion = (content: string): TicketSuggestion => {
    const priorities: Array<'low' | 'medium' | 'high' | 'critical'> = ['medium', 'high'];
    const categories = ['Technical Issue', 'Bug Report', 'Feature Request', 'General Support'];
    
    return {
      title: `Issue: ${content.substring(0, 50)}${content.length > 50 ? '...' : ''}`,
      description: content,
      priority: priorities[Math.floor(Math.random() * priorities.length)],
      category: categories[Math.floor(Math.random() * categories.length)],
      confidence: 0.85 + Math.random() * 0.15,
      suggestedAssignee: 'Auto-assign based on category'
    };
  };

  const handleCreateTicket = () => {
    if (!ticketSuggestion) return;

    const ticket = {
      id: `CB-${Date.now()}`,
      ...ticketSuggestion,
      status: 'open',
      createdAt: new Date(),
      createdBy: 'Conversational AI',
      messages: messages.filter(m => m.type === 'user').map(m => m.content)
    };

    if (onTicketCreated) {
      onTicketCreated(ticket);
    }

    const confirmationMessage: Message = {
      id: (Date.now() + 2).toString(),
      type: 'system',
      content: `✅ Ticket ${ticket.id} has been created successfully! You'll receive updates as we work on your issue.`,
      timestamp: new Date(),
      metadata: {
        ticketId: ticket.id,
        priority: ticket.priority,
        category: ticket.category
      }
    };

    setMessages(prev => [...prev, confirmationMessage]);
    setTicketSuggestion(null);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    setAttachments(prev => [...prev, ...files]);
  };

  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const toggleRecording = () => {
    setIsRecording(!isRecording);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'text-slate-400 bg-slate-500/20';
      case 'high': return 'text-slate-400 bg-slate-500/20';
      case 'medium': return 'text-slate-400 bg-slate-500/20';
      case 'low': return 'text-slate-400 bg-slate-500/20';
      default: return 'text-slate-400 bg-slate-500/20';
    }
  };

  return (
    <div className={`geex-card h-full flex flex-col ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-slate-700/30">
        <div className="flex items-center space-x-3">
          <div className="p-2 rounded-lg bg-slate-800/30">
            <MessageSquare size={20} className="text-slate-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold bg-gradient-to-r from-[#F6E7C1] to-[#E7CBA1] bg-clip-text text-transparent text-shadow">AI Support Chat</h3>
            <p className="text-sm text-slate-400">Conversational ticket creation</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <div className={`w-2 h-2 rounded-full ${isOnline ? 'bg-slate-400' : 'bg-slate-500'} animate-pulse`}></div>
          <span className="text-sm text-slate-400">{isOnline ? 'Online' : 'Offline'}</span>
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <AnimatePresence>
          {messages.map((message) => (
            <motion.div
              key={message.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div className={`max-w-[80%] ${message.type === 'user' ? 'order-2' : 'order-1'}`}>
                <div className={`flex items-start space-x-3 ${message.type === 'user' ? 'flex-row-reverse space-x-reverse' : ''}`}>
                  {/* Avatar */}
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    message.type === 'user' 
                      ? 'bg-slate-800/30' 
                      : message.type === 'ai'
                      ? 'bg-slate-800/30'
                      : 'bg-slate-800/30'
                  }`}>
                    {message.type === 'user' ? (
                      <User size={16} className="text-slate-400" />
                    ) : message.type === 'ai' ? (
                      <Bot size={16} className="text-slate-400" />
                    ) : (
                      <Zap size={16} className="text-slate-400" />
                    )}
                  </div>

                  {/* Message Content */}
                  <div className={`rounded-2xl p-4 ${
                    message.type === 'user'
                      ? 'bg-slate-800/30 border border-slate-500/30'
                      : message.type === 'ai'
                      ? 'bg-slate-800/50 border border-slate-700/30'
                      : 'bg-green-500/20 border border-green-500/30'
                  }`}>
                    <p className="text-white text-sm leading-relaxed">{message.content}</p>
                    
                    {/* Attachments */}
                    {message.metadata?.attachments && message.metadata.attachments.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {message.metadata.attachments.map((attachment, index) => (
                          <div key={index} className="flex items-center space-x-2 text-xs text-slate-400">
                            <Paperclip size={12} />
                            <span>{attachment}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Suggested Actions */}
                    {message.metadata?.suggestedActions && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {message.metadata.suggestedActions.map((action, index) => (
                          <Button
                            key={index}
                            variant="ghost"
                            size="sm"
                            className="text-xs bg-slate-700/50 hover:bg-slate-600/50 text-slate-300"
                          >
                            {action}
                          </Button>
                        ))}
                      </div>
                    )}

                    {/* Timestamp */}
                    <div className="mt-2 flex items-center space-x-1 text-xs text-slate-500">
                      <Clock size={10} />
                      <span>{message.timestamp.toLocaleTimeString()}</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>

        {/* Typing Indicator */}
        {isTyping && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex justify-start"
          >
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 rounded-full bg-slate-800/30 flex items-center justify-center">
                <Bot size={16} className="text-slate-400" />
              </div>
              <div className="bg-slate-800/50 border border-slate-700/30 rounded-2xl p-4">
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce"></div>
                  <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                  <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Ticket Suggestion */}
      {ticketSuggestion && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mx-4 mb-4 p-4 bg-slate-800/30 border border-slate-500/30 rounded-xl"
        >
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center space-x-2">
              <FileText size={16} className="text-slate-400" />
              <span className="text-sm font-medium text-white">Suggested Ticket</span>
              <span className="text-xs text-slate-400">({Math.round(ticketSuggestion.confidence * 100)}% confidence)</span>
            </div>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(ticketSuggestion.priority)}`}>
              {ticketSuggestion.priority.toUpperCase()}
            </span>
          </div>
          
          <h4 className="text-sm font-medium bg-gradient-to-r from-[#F6E7C1] to-[#E7CBA1] bg-clip-text text-transparent text-shadow mb-2">{ticketSuggestion.title}</h4>
          <p className="text-xs text-slate-400 mb-3">{ticketSuggestion.category}</p>
          
          <div className="flex space-x-2">
            <Button
              onClick={handleCreateTicket}
              className="bg-slate-600 hover:bg-slate-500 text-white text-xs"
              size="sm"
            >
              <CheckCircle size={14} className="mr-1" />
              Create Ticket
            </Button>
            <Button
              onClick={() => setTicketSuggestion(null)}
              variant="ghost"
              className="text-slate-400 hover:text-white text-xs"
              size="sm"
            >
              Dismiss
            </Button>
          </div>
        </motion.div>
      )}

      {/* Input Area */}
      <div className="p-4 border-t border-slate-700/30">
        {/* Attachments Preview */}
        {attachments.length > 0 && (
          <div className="mb-3 flex flex-wrap gap-2">
            {attachments.map((file, index) => (
              <div key={index} className="flex items-center space-x-2 bg-slate-800/50 rounded-lg px-3 py-2">
                <Paperclip size={14} className="text-slate-400" />
                <span className="text-xs text-slate-300">{file.name}</span>
                <button
                  onClick={() => removeAttachment(index)}
                  className="text-slate-400 hover:text-red-400"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="flex items-end space-x-3">
          <div className="flex-1 relative">
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Describe your issue or ask a question..."
              className="w-full bg-slate-800/50 border border-slate-600 rounded-xl px-4 py-3 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent resize-none"
            />
          </div>

          <div className="flex items-center space-x-2">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileUpload}
              className="hidden"
            />
            
            <Button
              onClick={() => fileInputRef.current?.click()}
              variant="ghost"
              size="sm"
              className="text-slate-400 hover:text-white p-2"
            >
              <Paperclip size={18} />
            </Button>

            <Button
              onClick={toggleRecording}
              variant="ghost"
              size="sm"
              className={`p-2 ${isRecording ? 'text-red-400 bg-red-500/20' : 'text-slate-400 hover:text-white'}`}
            >
              {isRecording ? <MicOff size={18} /> : <Mic size={18} />}
            </Button>

            <Button
              onClick={handleSendMessage}
              disabled={!inputValue.trim() && attachments.length === 0}
              className="bg-slate-600 hover:bg-slate-500 text-white p-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send size={18} />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConversationalTicketing;
